// Hooks for reading plans. Pages use these, never the source. main.tsx provides the API's PlanSource
// when VITE_API_URL is set; otherwise the prototype's (plansMock.ts).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import { mockPeople } from "./mock";
import { localDate, type Journey, type NewPlan, type PlanSource, type StartPlan } from "./plans";
import { createMockPlans } from "./plansMock";

export const PlanContext = createContext<PlanSource | null>(null);
let fallback: PlanSource | null = null;
export const usePlanSource = () => useContext(PlanContext) ?? (fallback ??= createMockPlans(mockPeople.me, mockPeople.friends));

export const planKeys = {
  all: ["plans"] as const,
  plans: ["plans", "list"] as const,
  plan: (id: string) => ["plans", "plan", id] as const,
  journeys: ["plans", "journeys"] as const,
  journey: (id: string) => ["plans", "journey", id] as const,
  invitations: ["plans", "invitations"] as const,
  notes: (id: string, day: number) => ["plans", "notes", id, day] as const,
};

export const usePlans = () => {
  const s = usePlanSource();
  return useQuery({ queryKey: planKeys.plans, queryFn: () => s.plans() });
};
/** A plan with its schedule. A catalogue plan never changes, so it is kept for the visit. */
export const usePlan = (id: string) => {
  const s = usePlanSource();
  return useQuery({ queryKey: planKeys.plan(id), queryFn: () => s.plan(id), staleTime: 10 * 60_000 });
};
export const useJourneys = () => {
  const s = usePlanSource();
  return useQuery({ queryKey: planKeys.journeys, queryFn: () => s.journeys() });
};
/** One journey with its schedule (the list carries plans without one). */
export const useJourney = (id: string) => {
  const s = usePlanSource();
  return useQuery({ queryKey: planKeys.journey(id), queryFn: () => s.journey(id) });
};
export const useInvitations = () => {
  const s = usePlanSource();
  return useQuery({ queryKey: planKeys.invitations, queryFn: () => s.invitations() });
};
export const useDayNotes = (journeyId: string, day: number, enabled = true) => {
  const s = usePlanSource();
  return useQuery({ queryKey: planKeys.notes(journeyId, day), queryFn: () => s.notes(journeyId, day), enabled });
};

export const useCreatePlan = () => {
  const s = usePlanSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewPlan) => s.createPlan(input),
    meta: { inline: true },
    onSuccess: (p) => {
      qc.setQueryData(planKeys.plan(p.id), p);
      void qc.invalidateQueries({ queryKey: planKeys.plans });
    },
  });
};

export const useStartPlan = () => {
  const s = usePlanSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, ...opts }: StartPlan & { planId: string }) => s.start(planId, opts),
    onSuccess: (j) => {
      qc.setQueryData(planKeys.journey(j.id), j);
      void qc.invalidateQueries({ queryKey: planKeys.journeys });
    },
  });
};

type JourneyAction =
  | { kind: "read"; day: number; read: boolean }
  | { kind: "pause"; paused: boolean }
  | { kind: "catch-up"; how: "mark-read" | "start-from-today" }
  | { kind: "invite"; userIds: string[] }
  | { kind: "withdraw"; userId: string };

/** Marking a day is optimistic: it shows at once and rolls back if the server refuses. */
export const useJourneyAction = (journeyId: string) => {
  const s = usePlanSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: JourneyAction): Promise<Journey | null> => {
      switch (a.kind) {
        case "read":
          return s.setRead(journeyId, a.day, a.read);
        case "pause":
          return s.setPaused(journeyId, a.paused);
        case "catch-up":
          return s.catchUp(journeyId, a.how, localDate());
        case "invite":
          await s.invite(journeyId, a.userIds);
          return null;
        case "withdraw":
          await s.withdrawInvite(journeyId, a.userId);
          return null;
      }
    },
    onMutate: async (a) => {
      if (a.kind !== "read") return undefined;
      await qc.cancelQueries({ queryKey: planKeys.journey(journeyId) });
      const before = qc.getQueryData<Journey | null>(planKeys.journey(journeyId));
      if (before) {
        const read = a.read ? [...new Set([...before.me.read, a.day])].sort((x, y) => x - y) : before.me.read.filter((d) => d !== a.day);
        qc.setQueryData<Journey>(planKeys.journey(journeyId), {
          ...before,
          me: { ...before.me, read },
          members: before.members.map((m) => (m.isMe ? { ...m, read } : m)),
        });
      }
      return { before };
    },
    onError: (_e, _a, ctx) => {
      if (ctx?.before) qc.setQueryData(planKeys.journey(journeyId), ctx.before);
    },
    onSuccess: (j) => {
      if (j) qc.setQueryData(planKeys.journey(journeyId), j);
      else void qc.invalidateQueries({ queryKey: planKeys.journey(journeyId) });
      void qc.invalidateQueries({ queryKey: planKeys.journeys });
    },
  });
};

export const useLeavePlan = () => {
  const s = usePlanSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => s.leave(id),
    onSuccess: (_v, id) => {
      qc.removeQueries({ queryKey: planKeys.journey(id) });
      void qc.invalidateQueries({ queryKey: planKeys.journeys });
    },
  });
};

export const useRespondInvitation = () => {
  const s = usePlanSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ journeyId, accept }: { journeyId: string; accept: boolean }) => s.respond(journeyId, accept),
    onSuccess: (j) => {
      if (j) qc.setQueryData(planKeys.journey(j.id), j);
      void qc.invalidateQueries({ queryKey: planKeys.all });
      void qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
};

/** Joining from an invite link. The page says why, if the link can't be used. */
export const useJoinPlan = () => {
  const s = usePlanSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => s.join(code),
    meta: { inline: true },
    onSuccess: (j) => {
      qc.setQueryData(planKeys.journey(j.id), j);
      void qc.invalidateQueries({ queryKey: planKeys.all });
    },
  });
};

export const useAddDayNote = (journeyId: string, day: number) => {
  const s = usePlanSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => s.addNote(journeyId, day, body),
    // The refusal (the word filter, too fast) shows beside the text, which is kept.
    meta: { inline: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: planKeys.notes(journeyId, day) }),
  });
};

export const useDeleteDayNote = (journeyId: string, day: number) => {
  const s = usePlanSource();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => s.deleteNote(journeyId, noteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: planKeys.notes(journeyId, day) }),
  });
};
